import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { Observable, iif, throwError } from 'rxjs'
import { catchError, first, switchMap } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { OAuth } from '../../models/oauth'
import { State, getAccessTokenState } from '../../reducers'
import { AuthService } from './auth.service'

@Injectable()
export class HubtypeAuthInterceptor implements HttpInterceptor {
  public static isHubtypeAuthenticableUrl(uri: string): boolean {
    return (
      uri?.startsWith(environment.baseURL) &&
      !uri.startsWith(`${environment.baseURL}/_auth/app/v1/auth/login`) &&
      !uri.startsWith(`${environment.baseURL}/o/token`)
    )
  }

  constructor(
    private store: Store<State>,
    private authService: AuthService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const isRequestAHubtypeAuthenticableUri =
      HubtypeAuthInterceptor.isHubtypeAuthenticableUrl(request.url)
    return iif(
      () => isRequestAHubtypeAuthenticableUri,
      this.store.select(getAccessTokenState).pipe(
        first(),
        switchMap(oAuth => next.handle(this.assertHubtypeAuth(request, oAuth)))
      ),
      next.handle(request)
    ).pipe(
      catchError(err => {
        if (
          isRequestAHubtypeAuthenticableUri &&
          this.isUnauthenticatedExpiredTokenError(err)
        ) {
          return this.authService
            .refreshSession()
            .pipe(
              switchMap(oAuth =>
                next.handle(this.assertHubtypeAuth(request, oAuth))
              )
            )
        } else {
          if (
            isRequestAHubtypeAuthenticableUri &&
            this.isUnauthenticatedError(err)
          ) {
            this.authService.logout(null, 'Auth error')
          }
          return throwError(err)
        }
      })
    )
  }

  private assertHubtypeAuth(
    request: HttpRequest<unknown>,
    oAuth: OAuth
  ): HttpRequest<unknown> {
    if (!oAuth) {
      return request
    }
    let newHeaders = request.headers
    if (!this.isAccessTokenForbidden(request.url)) {
      newHeaders = newHeaders.set(
        'Authorization',
        `${oAuth.token_type} ${oAuth.access_token}`
      )
    }
    if (this.isSessionTokenRequired(request.url)) {
      newHeaders = newHeaders.set('X-Session-Token', `${oAuth.session_token}`)
    }
    return request.clone({ headers: newHeaders })
  }

  private isUnauthenticatedError(err: HttpErrorResponse): boolean {
    return (
      (err.status === 401 || err.status === 410) &&
      !this.isReauthenticateFlow(err)
    )
  }

  private isUnauthenticatedExpiredTokenError(err: HttpErrorResponse): boolean {
    /*
     * We consider any 401 error but the cookie related errors as expired token.
     * This can be improved in the future.
     */
    if (err.status !== 401 && err.status !== 410) {
      return false
    } else if (['missing_cookie', 'invalid_cookie'].includes(err.error?.code)) {
      return false
    } else if (this.isReauthenticateFlow(err)) {
      return false
    }
    return true
  }

  private isSessionTokenRequired(uri: string): boolean {
    return (
      uri.startsWith(`${environment.baseURL}/_auth/`) ||
      uri.startsWith(`${environment.baseURL}/o/token`)
    )
  }

  private isAccessTokenForbidden(uri: string): boolean {
    return uri.startsWith(`${environment.baseURL}/_auth/`)
  }

  private isReauthenticateFlow(err: HttpErrorResponse): boolean {
    return err.error.data?.flows?.find(flow => flow.id === 'reauthenticate')
  }
}
